import React, { useState, useEffect } from 'react';
import {
    Plus, CheckCircle2, Clock, AlertTriangle,
    User, Calendar, MoreVertical, X, Trash2, Save, RotateCw
} from 'lucide-react';
import api from '../api/axiosInstance';

const ProjectTasks = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [tasks, setTasks] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [staff, setStaff] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Combined state for Add/Edit
    const [taskForm, setTaskForm] = useState({
        id: null, task_name: '', description: '', priority: 'medium',
        assigned_to: '', due_date: '', status: 'pending', progress_percent: 0
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedProjectId) fetchTasks();
    }, [selectedProjectId]);

    const fetchInitialData = async () => {
        setIsLoading(true);
        try {
            const [pRes, sRes] = await Promise.all([
                api.get('get_projects.php'),
                api.get('get_staff.php')
            ]);
            setProjects(pRes?.data?.projects || []);
            setStaff(sRes?.data?.staff || []);
        } catch (err) {
            console.error("Error loading tasks initial data:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTasks = async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`get_tasks.php?project_id=${selectedProjectId}`);
            setTasks(res?.data?.tasks || []);
        } catch (err) {
            console.error("Error fetching tasks:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReload = async () => {
        setIsLoading(true);
        try {
            const [pRes, sRes] = await Promise.all([
                api.get('get_projects.php'),
                api.get('get_staff.php')
            ]);
            setProjects(pRes?.data?.projects || []);
            setStaff(sRes?.data?.staff || []);
            if (selectedProjectId) {
                const res = await api.get(`get_tasks.php?project_id=${selectedProjectId}`);
                setTasks(res?.data?.tasks || []);
            }
        } catch (err) {
            console.error("Error reloading tasks data:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveTask = async (e) => {
        e.preventDefault();
        // Determine if we are updating or creating
        const endpoint = taskForm.id ? 'update_task_full.php' : 'save_task.php';
        const res = await api.post(endpoint, { ...taskForm, project_id: selectedProjectId });

        if (res.data.status === 'success') {
            setIsModalOpen(false);
            resetForm();
            fetchTasks();
        }
    };

    const handleDeleteTask = async (e, taskId) => {
        e.stopPropagation(); // Prevent opening the modal
        if (!window.confirm("Are you sure you want to delete this milestone?")) return;

        const res = await api.post('delete_task.php', { task_id: taskId });
        if (res.data.status === 'success') {
            fetchTasks();
            setIsModalOpen(false);
        }
    };

    const openEditModal = (task) => {
        setTaskForm({
            id: task.id,
            task_name: task.task_name,
            description: task.description,
            priority: task.priority,
            assigned_to: task.assigned_to,
            due_date: task.due_date,
            status: task.status,
            progress_percent: task.progress_percent
        });
        setIsModalOpen(true);
    };

    const resetForm = () => setTaskForm({ id: null, task_name: '', description: '', priority: 'medium', assigned_to: '', due_date: '', status: 'pending', progress_percent: 0 });

    const updateProgressSlider = async (e, taskId, newProgress) => {
        e.stopPropagation(); // Prevent modal from opening when sliding
        const status = newProgress === 100 ? 'completed' : (newProgress > 0 ? 'in_progress' : 'pending');
        await api.post('update_task_status.php', { task_id: taskId, progress: newProgress, status });
        fetchTasks();
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">Work Progress</h1>
                    <select
                        className="mt-2 bg-transparent border-none font-bold text-indigo-600 outline-none cursor-pointer text-lg"
                        value={selectedProjectId}
                        onChange={(e) => setSelectedProjectId(e.target.value)}
                    >
                        <option value="">Select Project Site...</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleReload}
                        disabled={isLoading}
                        title="Reload Tasks & Projects Data"
                        className="bg-white hover:bg-slate-50 text-slate-700 px-5 py-4 rounded-2xl font-bold flex items-center gap-2 border border-slate-200 shadow-sm transition-all active:scale-95 disabled:opacity-60"
                    >
                        <RotateCw size={18} className={`text-indigo-600 ${isLoading ? 'animate-spin' : ''}`} />
                        <span>{isLoading ? 'Reloading...' : 'Reload'}</span>
                    </button>
                    {selectedProjectId && (
                        <button onClick={() => { resetForm(); setIsModalOpen(true); }} className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-2 shadow-xl hover:bg-slate-800 transition-all active:scale-95">
                            <Plus size={18} /> Add Milestone
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {['pending', 'in_progress', 'completed'].map(colStatus => (
                    <div key={colStatus} className="space-y-4">
                        <div className="flex justify-between items-center px-2">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{colStatus.replace('_', ' ')}</h3>
                            <span className="bg-slate-100 text-slate-500 text-[10px] font-black px-2 py-0.5 rounded-full">
                                {tasks.filter(t => t.status === colStatus).length}
                            </span>
                        </div>

                        <div className="space-y-4">
                            {tasks.filter(t => t.status === colStatus).map(task => (
                                <div
                                    key={task.id}
                                    onClick={() => openEditModal(task)}
                                    className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-xl hover:border-indigo-100 transition-all group cursor-pointer relative"
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase ${task.priority === 'urgent' ? 'bg-rose-50 text-rose-600' : 'bg-indigo-50 text-indigo-600'
                                            }`}>
                                            {task.priority}
                                        </div>
                                        <button
                                            onClick={(e) => handleDeleteTask(e, task.id)}
                                            className="text-slate-200 hover:text-rose-500 transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>

                                    <h4 className="font-black text-slate-900 leading-tight mb-2 group-hover:text-indigo-600 transition-colors">{task.task_name}</h4>
                                    <p className="text-xs text-slate-400 line-clamp-2 mb-6">{task.description}</p>

                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center text-[10px] font-bold">
                                            <span className="text-slate-400 uppercase">Progress</span>
                                            <span className="text-indigo-600 font-black">{task.progress_percent}%</span>
                                        </div>
                                        <input
                                            type="range"
                                            className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                            value={task.progress_percent}
                                            onClick={(e) => e.stopPropagation()} // Stop modal from opening on slider click
                                            onChange={(e) => updateProgressSlider(e, task.id, e.target.value)}
                                        />
                                        <div className="flex justify-between items-center pt-4 border-t border-slate-50">
                                            <div className="flex items-center gap-2 text-slate-500">
                                                <User size={12} className="text-indigo-400" />
                                                <span className="text-[10px] font-black uppercase">{task.staff_name || 'Unassigned'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <Calendar size={12} />
                                                <span className="text-[10px] font-black uppercase">{task.due_date}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* TASK MODAL (ADD / EDIT) */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleSaveTask} className="bg-white w-full max-w-2xl rounded-[48px] shadow-2xl p-12 space-y-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-3xl font-black italic tracking-tighter">
                                {taskForm.id ? 'Edit Milestone' : 'Add Milestone'}
                            </h2>
                            <button type="button" onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full"><X /></button>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Milestone Title</label>
                            <input
                                required
                                value={taskForm.task_name}
                                placeholder="e.g. Living Room False Ceiling"
                                className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-indigo-500 transition-all"
                                onChange={e => setTaskForm({ ...taskForm, task_name: e.target.value })}
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Instructions</label>
                            <textarea
                                value={taskForm.description}
                                placeholder="Details about material or finish..."
                                className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm h-28 outline-none border-2 border-transparent focus:border-indigo-500 transition-all"
                                onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Assign Staff</label>
                                <select
                                    className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none"
                                    value={taskForm.assigned_to}
                                    onChange={e => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                                >
                                    <option value="">Choose Staff...</option>
                                    {staff.map(s => <option key={s.id} value={s.id}>{s.username}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Deadline</label>
                                <input
                                    type="date"
                                    className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none"
                                    value={taskForm.due_date}
                                    onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Priority</label>
                                <select
                                    className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none"
                                    value={taskForm.priority}
                                    onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Current Status</label>
                                <select
                                    className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none"
                                    value={taskForm.status}
                                    onChange={e => setTaskForm({ ...taskForm, status: e.target.value })}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                    <option value="halted">Halted</option>
                                </select>
                            </div>
                        </div>

                        <button className="w-full py-6 bg-indigo-600 text-white rounded-[32px] font-black text-xl shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">
                            <Save size={24} /> {taskForm.id ? 'Update Milestone' : 'Launch Milestone'}
                        </button>

                        {taskForm.id && (
                            <button
                                type="button"
                                onClick={(e) => handleDeleteTask(e, taskForm.id)}
                                className="w-full text-rose-500 font-black text-xs uppercase tracking-widest hover:underline"
                            >
                                Delete this task permanently
                            </button>
                        )}
                    </form>
                </div>
            )}
        </div>
    );
};

export default ProjectTasks;